import { useState, useEffect } from "react";
import { ArrowDown } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import LetterCarousel from "@/components/LetterCarousel";
import HowItWorks from "@/components/HowItWorks";
import OrderForm from "@/components/OrderForm";
import FloatingEphemera from "@/components/FloatingEphemera";
import PaymentSuccessToast from "@/components/PaymentSuccessToast";
import IndexSkeleton from "@/components/IndexSkeleton";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useToast } from "@/hooks/use-toast";
import { useScrollTo } from "@/hooks/use-scroll-to";

const Index = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { scrollToElement } = useScrollTo();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (searchParams.get("canceled") === "true") {
      toast({
        title: "Payment canceled",
        description: "No worries! Your order was not processed.",
        variant: "default"
      });
      navigate("/", { replace: true });
      setTimeout(() => scrollToElement("order"), 100);
    }
  }, [searchParams, navigate, toast, scrollToElement]);

  if (isLoading) {
    return <IndexSkeleton />;
  }

  return (
    <div className="min-h-screen bg-background relative">
      <SEO
        title="Eunice — Personalized Travel Letters from Locals"
        description="Get authentic travel recommendations for any city. $1.99 AI-generated letters with local insights, hidden gems, and insider tips. Delivered to your inbox."
        canonical="/"
      />
      <PaymentSuccessToast />
      <FloatingEphemera />

      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex flex-col items-center justify-center px-6 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center relative z-10">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.25em] text-muted-foreground mb-4 md:mb-6 font-sans opacity-0 animate-fade-up">
              A different kind of travel guide
            </p>

            <h1 className="font-serif text-[2.5rem] leading-[1.15] md:text-6xl lg:text-7xl text-foreground md:leading-[1.1] mb-5 md:mb-6 opacity-0 animate-fade-up animation-delay-100">
              Letters from <span className="text-primary">Eunice</span>
            </h1>

            <p className="text-lg md:text-2xl text-charcoal-light leading-relaxed mb-6 md:mb-8 font-serif opacity-0 animate-fade-up animation-delay-200">
              Travel recommendations from an elderly woman who has spent her life wandering the world. Not tips. Not hacks.{" "}
              <em className="text-primary">Stories.</em>
            </p>

            <div className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2.5 md:py-3 rounded-full bg-cream-dark/80 border border-tan/50 mb-8 md:mb-10 opacity-0 animate-fade-up animation-delay-300">
              <span className="font-serif text-base md:text-lg text-foreground">
                One city, one letter
              </span>
              <span className="h-4 w-px bg-tan" aria-hidden="true" />
              <span className="font-serif text-base md:text-lg text-primary font-semibold">
                $1.99
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 opacity-0 animate-fade-up animation-delay-400">
              <Button variant="eunice" size="xl" onClick={() => scrollToElement("order")} className="w-full sm:w-auto min-h-[48px]">
                Request a Letter
              </Button>
              <Button variant="eunice-outline" size="lg" onClick={() => scrollToElement("letters")} className="w-full sm:w-auto min-h-[48px]">
                Read Samples
                <ArrowDown className="h-4 w-4 ml-1" aria-hidden="true" />
              </Button>
            </div>
          </div>

          <button 
            onClick={() => scrollToElement("letters")} 
            className="absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 opacity-40 hover:opacity-70 transition-opacity cursor-pointer" 
            aria-label="Scroll to sample letters"
          >
            <div className="flex flex-col items-center gap-2 animate-bounce">
              <ArrowDown className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
          </button>
        </section>

        {/* Sample Letters Section */}
        <section id="letters" className="relative py-16 md:py-24 px-4 md:px-6" aria-labelledby="sample-letters-heading">
          <div className="max-w-4xl mx-auto">
            <header className="text-center mb-10 md:mb-16 px-2">
              <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-muted-foreground mb-2 md:mb-3 font-sans">
                Sample Letters
              </p>
              <h2 id="sample-letters-heading" className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-3 md:mb-4">
                What you get
              </h2>
              <p className="text-base md:text-lg text-charcoal-light max-w-xl mx-auto font-sans">
                These are excerpts from letters Eunice has written. Each one is as
                unique as the person who receives it.
              </p>
            </header>

            <LetterCarousel />
          </div>
        </section>

        <HowItWorks />

        {/* About Eunice Section */}
        <section className="relative py-16 md:py-24 px-6 bg-cream-dark/30" aria-labelledby="why-eunice-heading">
          <div className="max-w-2xl mx-auto text-center">
            <h2 id="why-eunice-heading" className="sr-only">Why Eunice is different</h2>
            <div className="inline-block px-4 py-2 rounded-full bg-tan/20 border border-tan/30 mb-6 md:mb-8">
              <span className="font-serif text-sm text-charcoal-light italic">
                Who is Eunice?
              </span>
            </div>

            <blockquote className="font-serif text-xl md:text-2xl lg:text-3xl text-foreground leading-relaxed mb-6 md:mb-8 italic px-2">
              "I have been writing letters for sixty years—to friends, to lovers,
              to strangers who became family. Travel is not about seeing places.
              It is about becoming someone you couldn't have been at home."
            </blockquote>

            <div className="flex items-center justify-center gap-3 md:gap-4 flex-wrap">
              <div className="hidden md:block h-px w-16 bg-tan" aria-hidden="true" />
              <p className="font-sans text-xs md:text-sm text-muted-foreground uppercase tracking-wider">
                167 countries • Still traveling
              </p>
              <div className="hidden md:block h-px w-16 bg-tan" aria-hidden="true" />
            </div>
          </div>
        </section>

        {/* Order Section */}
        <section id="order" className="relative py-16 md:py-24 px-4 md:px-6" aria-labelledby="order-heading">
          <div className="max-w-lg mx-auto">
            <header className="text-center mb-8 md:mb-12 px-2">
              <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-muted-foreground mb-2 md:mb-3 font-sans">
                Request Your Letter
              </p>
              <h2 id="order-heading" className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-3 md:mb-4">
                Where to, traveler?
              </h2>
              <p className="text-base md:text-lg text-charcoal-light font-sans">
                Tell Eunice your destination, and she'll write you a letter from
                her memories of that place.
              </p>
            </header>

            <div className="letter-card mx-2 md:mx-0">
              <OrderForm />
            </div>

            <p className="text-center mt-6 text-sm text-muted-foreground font-sans">
              Have questions?{" "}
              <Link to="/faq" className="text-primary hover:underline">
                Read our FAQ
              </Link>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
